
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { 
  Users, 
  UserCheck, 
  BarChart3, 
  Settings, 
  Shield, 
  Award, 
  Home,
  BookOpen, 
  Database, 
  Wrench,
  FileText,
  Brain,
  Activity,
  Zap,
  FileWarning
} from 'lucide-react';
import { useSupabaseAuth } from '@/hooks/auth';
import { useSearchParams } from 'react-router-dom';
import { CourseCountBadge } from '@/components/common/CourseCountBadge';
import { supabase } from '@/integrations/supabase/client';
import UserManagement from '@/pages/admin/UserManagement';
import EnhancedApprovalInterface from './EnhancedApprovalInterface';
import AnalyticsDashboard from './AnalyticsDashboard';
import AdvancedStatusManager from './AdvancedStatusManager';
import EnhancedCertificateManagement from './EnhancedCertificateManagement';
import AdminInviteUser from './AdminInviteUser';
import AdminCourseManagement from './AdminCourseManagement';
import CourseMigrationPanel from './CourseMigrationPanel';
import { ContentQualityDashboard } from './ContentQualityDashboard';
import { ContentQualityDashboard as MLContentQualityDashboard } from '@/components/ml/ContentQualityDashboard';
import { CourseIntegrationDashboard } from './CourseIntegrationDashboard';
import UserExperienceDashboard from './UserExperienceDashboard';
import CourseHealthDashboard from './CourseHealthDashboard';
import AdminOverviewTab from './AdminOverviewTab';
import { StudentProgressReport } from '@/components/reports/StudentProgressReport';
import AdminAuthDebug from '@/components/debug/AdminAuthDebug';
import RBACTester from '@/components/debug/RBACTester';
import CourseAssignmentValidator from '@/components/debug/CourseAssignmentValidator';
import SystemHealthCheck from '@/components/debug/SystemHealthCheck';
import AdminStaffFunctionTester from './AdminStaffFunctionTester';
import AdminStaffImplementationSummary from './AdminStaffImplementationSummary';
import { AutoMigrationIndicator } from '@/components/courses/AutoMigrationIndicator';
import CoursesMigrationTrigger from '@/components/courses/CoursesMigrationTrigger';
import MasterControlPanel from './MasterControlPanel';
import MigrationExecutor from './MigrationExecutor';
import ContentSyncDashboard from './ContentSyncDashboard';
import CourseIntegrationStatus from '@/components/debug/CourseIntegrationStatus';
import AuthStatus from '@/components/debug/AuthStatus';
import { useCourses } from '@/hooks/useCourses';
import { ContentEnhancementStats } from '@/components/dashboard/ContentEnhancementStats';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ContentDiagnosticsPanel } from '@/components/admin/ContentDiagnosticsPanel';
import { ContentReviewPanel } from '@/components/admin/ContentReviewPanel';
import { BulkOperationsPanel } from '@/components/admin/BulkOperationsPanel';
import { SystemTestingPanel } from '@/components/admin/SystemTestingPanel';
import { PerformanceOptimizationPanel } from '@/components/admin/PerformanceOptimizationPanel';
import { ProductionReadinessPanel } from '@/components/admin/ProductionReadinessPanel';
import { QuestionGenerationPanel } from '@/components/admin/QuestionGenerationPanel';
import { UnifiedQuestionDashboard } from '@/components/admin/UnifiedQuestionDashboard';
import { AdvancedQuestionTools } from '@/components/admin/AdvancedQuestionTools';
import { DatabaseIntegrityMonitor } from './DatabaseIntegrityMonitor';
import { GenerationHealthDashboard } from './GenerationHealthDashboard';
import { QuizCoverageReport } from './QuizCoverageReport';
import { QuizGenerationMonitor } from './QuizGenerationMonitor';
import { QuizCompletionDashboard } from './QuizCompletionDashboard';
import FixCourseAssignments from './FixCourseAssignments';
import { useActiveQuizGeneration } from '@/hooks/useActiveQuizGeneration';
import { useStaleOperationMonitor } from '@/hooks/useStaleOperationMonitor';
import { GenerationStatusMonitor } from './GenerationStatusMonitor';
import { ModuleDataQuality } from '@/pages/admin/ModuleDataQuality';
import { ContentDivisionDashboard } from '@/components/admin/ContentDivisionDashboard';
import { ContentFixPanel } from './ContentFixPanel';

const AdminDashboard: React.FC = () => {
  const { hasAccess } = useSupabaseAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const activeSubTab = searchParams.get('subtab') || '';
  const activeView = searchParams.get('view') || '';

  // Monitor quiz generation and stale operations
  useActiveQuizGeneration();
  useStaleOperationMonitor();
  const { autoMigration } = useCourses();
  const [moduleStats, setModuleStats] = React.useState<{
    totalIncomplete: number;
    withContent: number;
    withoutContent: number;
  } | null>(null);

  const [audioStats, setAudioStats] = React.useState<{
    total: number;
    withAudio: number;
    missing: number;
  }>({ total: 0, withAudio: 0, missing: 0 });

  React.useEffect(() => {
    const loadModuleStats = async () => {
      try {
        const { ModuleGenerationService } = await import('@/services/ModuleGenerationService');
        const stats = await ModuleGenerationService.getIncompleteCourseStats();
        setModuleStats({
          totalIncomplete: stats.totalIncomplete,
          withContent: stats.withContent,
          withoutContent: stats.withoutContent
        });
      } catch (error) {
        console.error('Failed to load module stats:', error);
      }
    };
    loadModuleStats();
  }, []);

  React.useEffect(() => {
    const loadAudioStats = async () => {
      try {
        // Get total ACTIVE modules from course_modules (is_enabled = true)
        const { count: totalModules } = await supabase
          .from('course_modules')
          .select('module_id', { count: 'exact', head: true })
          .eq('is_enabled', true);

        // Get module IDs for enabled modules
        const { data: enabledModules } = await supabase
          .from('course_modules')
          .select('module_id')
          .eq('is_enabled', true);

        const moduleIds = enabledModules?.map(m => m.module_id) || [];

        // Get modules with audio from the enabled set
        const { data: audioData } = await supabase
          .from('module_audio')
          .select('module_id')
          .not('audio_url', 'is', null)
          .in('module_id', moduleIds);

        const total = totalModules || 0;
        const withAudio = audioData?.length || 0;
        const missing = total - withAudio;

        setAudioStats({ total, withAudio, missing });
      } catch (error) {
        console.error('Failed to load audio stats:', error);
      }
    };
    loadAudioStats();
  }, [activeTab]);

  const [enhancedCount, setEnhancedCount] = React.useState<number>(0);

  // Fetch enhanced content count for protection status
  React.useEffect(() => {
    const fetchEnhancedCount = async () => {
      const { count } = await supabase
        .from('module_content')
        .select('*', { count: 'exact', head: true })
        .gte('content_word_count', 1000);
      setEnhancedCount(count || 0);
    };
    fetchEnhancedCount();
    
    // Refresh count when tab changes
    const interval = setInterval(fetchEnhancedCount, 10000); // Update every 10 seconds
    return () => clearInterval(interval);
  }, [activeTab]);

  if (!hasAccess('admin')) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-muted-foreground">You don't have admin permissions to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Advanced certificate management, user administration, and system controls
          </p>
          <div className="mt-3">
            <CourseCountBadge showIcons={true} />
          </div>
        </div>
        <Badge variant="secondary" className="flex items-center space-x-2">
          <Shield className="h-4 w-4" />
          <span>Administrator</span>
        </Badge>
      </div>

      <Tabs 
        value={activeTab} 
        onValueChange={(value) => setSearchParams({ tab: value })} 
        className="w-full"
      >
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Home className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="courses" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Courses
          </TabsTrigger>
          <TabsTrigger value="certificates" className="flex items-center gap-2">
            <Award className="h-4 w-4" />
            Certificates
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            System
          </TabsTrigger>
          <TabsTrigger value="tools" className="flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Tools
          </TabsTrigger>
          <TabsTrigger value="reports" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Reports
          </TabsTrigger>
          <TabsTrigger value="configuration" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Configuration
            {autoMigration.isRunning && (
              <Badge variant="secondary" className="ml-1">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse mr-1"></span>
                Active
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="questions" className="flex items-center gap-2">
            <Brain className="h-4 w-4" />
            Questions
          </TabsTrigger>
          <TabsTrigger value="content-division" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Content Division
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab - NEW */}
        <TabsContent value="overview" className="space-y-6">
          <AdminOverviewTab />
          <MasterControlPanel />
          <DatabaseIntegrityMonitor />
        </TabsContent>

        {/* Users Tab - Consolidated with sub-tabs */}
        <TabsContent value="users" className="space-y-6">
          <Tabs 
            value={activeSubTab || 'all'} 
            onValueChange={(value) => setSearchParams({ tab: 'users', subtab: value })}
          >
            <TabsList>
              <TabsTrigger value="all">All Users</TabsTrigger>
              <TabsTrigger value="approvals">Approvals</TabsTrigger>
              <TabsTrigger value="invite">Invite Users</TabsTrigger>
            </TabsList>
            <TabsContent value="all" className="mt-6">
              <UserManagement />
            </TabsContent>
            <TabsContent value="approvals" className="mt-6">
              <EnhancedApprovalInterface />
            </TabsContent>
            <TabsContent value="invite" className="mt-6">
              <AdminInviteUser />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Courses Tab - Consolidated with sub-tabs */}
        <TabsContent value="courses" className="space-y-6">
          <Tabs 
            value={activeSubTab || 'management'} 
            onValueChange={(value) => setSearchParams({ tab: 'courses', subtab: value })}
          >
            <TabsList>
              <TabsTrigger value="management">Management</TabsTrigger>
              <TabsTrigger value="integration">Integration</TabsTrigger>
              <TabsTrigger value="quality">Quality</TabsTrigger>
              <TabsTrigger value="migration">Migration</TabsTrigger>
            </TabsList>
            <TabsContent value="management" className="mt-6">
              <AdminCourseManagement />
            </TabsContent>
            <TabsContent value="integration" className="mt-6">
              <div className="space-y-6">
                <CourseIntegrationStatus />
                <ContentSyncDashboard />
                <CourseIntegrationDashboard />
              </div>
            </TabsContent>
            <TabsContent value="quality" className="mt-6">
              <ContentQualityDashboard />
            </TabsContent>
            <TabsContent value="migration" className="mt-6">
              <div className="space-y-6">
                <AutoMigrationIndicator 
                  isRunning={autoMigration.isRunning}
                  progress={autoMigration.progress}
                  total={autoMigration.total}
                  completed={autoMigration.completed}
                  errors={autoMigration.errors}
                  hasRun={autoMigration.hasRun}
                />
                <CoursesMigrationTrigger />
                <MigrationExecutor />
                <CourseMigrationPanel />
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Certificates Tab */}
        <TabsContent value="certificates" className="space-y-6">
          <EnhancedCertificateManagement />
        </TabsContent>

        {/* Analytics Tab - Enhanced with User Experience */}
        <TabsContent value="analytics" className="space-y-6">
          <Tabs 
            value={activeSubTab || 'system'} 
            onValueChange={(value) => setSearchParams({ tab: 'analytics', subtab: value })}
          >
            <TabsList>
              <TabsTrigger value="system">System</TabsTrigger>
              <TabsTrigger value="user-experience">User Experience</TabsTrigger>
            </TabsList>
            <TabsContent value="system" className="mt-6">
              <AnalyticsDashboard />
            </TabsContent>
            <TabsContent value="user-experience" className="mt-6">
              <UserExperienceDashboard />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* System Tab - Consolidated */}
        <TabsContent value="system" className="space-y-6">
          <Tabs 
            value={activeSubTab || 'status'} 
            onValueChange={(value) => setSearchParams({ tab: 'system', subtab: value })}
          >
            <TabsList>
              <TabsTrigger value="status">Status</TabsTrigger>
              <TabsTrigger value="health">Data Health</TabsTrigger>
            </TabsList>
            <TabsContent value="status" className="mt-6">
              <AdvancedStatusManager />
            </TabsContent>
            <TabsContent value="health" className="mt-6">
              <CourseHealthDashboard />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Tools Tab - Debug & Testing Tools */}
        <TabsContent value="tools" className="space-y-6">
          <Tabs defaultValue="content-fix" className="w-full">
            <TabsList>
              <TabsTrigger value="content-fix" className="flex items-center gap-2">
                <FileWarning className="h-4 w-4" />
                Content Fix
              </TabsTrigger>
              <TabsTrigger value="debug">Debug Tools</TabsTrigger>
            </TabsList>
            
            <TabsContent value="content-fix" className="mt-6">
              <ContentFixPanel />
            </TabsContent>
            
            <TabsContent value="debug" className="mt-6">
              <Alert className="mb-6">
                <Wrench className="h-4 w-4" />
                <AlertDescription>
                  Admin debugging and testing tools. Use these to verify system functionality and troubleshoot issues.
                </AlertDescription>
              </Alert>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <AuthStatus />
                <AdminAuthDebug />
                <RBACTester />
                <SystemHealthCheck />
                <CourseAssignmentValidator />
                <AdminStaffFunctionTester />
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-primary" />
                      AI Resource Generator
                    </CardTitle>
                    <CardDescription>
                      Automatically generate educational resources for course modules using AI
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      onClick={() => window.location.href = '/admin/resource-generator'}
                      className="w-full"
                    >
                      Open Resource Generator
                    </Button>
                  </CardContent>
                </Card>
              </div>
              
              <AdminStaffImplementationSummary />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Reports Tab - NEW */}
        <TabsContent value="reports" className="space-y-6">
          <StudentProgressReport />
        </TabsContent>

        {/* Questions Tab - Interview Question Generation */}
        <TabsContent value="questions" className="space-y-6">
          <Alert className="mb-6">
            <Brain className="h-4 w-4" />
            <AlertDescription>
              Unified 100k question generation system with intelligent role distribution across 64 roles. 
              Questions are automatically balanced, deduplicated, and tailored to each role's specific context.
            </AlertDescription>
          </Alert>
          
          <Tabs defaultValue="generation" className="w-full">
            <TabsList>
              <TabsTrigger value="generation">100k Generation System</TabsTrigger>
              <TabsTrigger value="advanced">Advanced Tools</TabsTrigger>
            </TabsList>
            
            <TabsContent value="generation" className="mt-6">
              <UnifiedQuestionDashboard />
            </TabsContent>
            
            <TabsContent value="advanced" className="mt-6">
              <AdvancedQuestionTools />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Configuration Tab - Content Enhancement & System Settings */}
        <TabsContent value="configuration" className="space-y-6">
          {enhancedCount > 50 && (
            <Alert className="mb-6 border-green-500 bg-green-50 dark:bg-green-950">
              <Shield className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-900 dark:text-green-100">Enhanced Content Protection Active</AlertTitle>
              <AlertDescription className="text-green-800 dark:text-green-200">
                <strong>{enhancedCount} modules</strong> have been enhanced with AI-generated content (1000+ words each). 
                Auto-migration is <strong>disabled</strong> to protect this content. Manual migration will overwrite 
                all enhanced modules. Only proceed with migration if you need to restore from JSON files.
              </AlertDescription>
            </Alert>
          )}
          
          <Alert className="mb-6">
            <Database className="h-4 w-4" />
            <AlertDescription>
              Advanced content enhancement, AI generation monitoring, and system configuration tools. These tools manage automated content improvement and operational workflows.
            </AlertDescription>
          </Alert>
          
          <Tabs 
            value={activeSubTab || 'enhancement'} 
            onValueChange={(value) => setSearchParams({ tab: 'configuration', subtab: value })}
          >
            <TabsList>
              <TabsTrigger value="enhancement">Content Enhancement</TabsTrigger>
              <TabsTrigger value="generation">AI Generation</TabsTrigger>
              <TabsTrigger value="data-quality" className="flex items-center gap-2">
                <FileWarning className="h-4 w-4" />
                Data Quality
              </TabsTrigger>
              <TabsTrigger value="review">Content Review</TabsTrigger>
              <TabsTrigger value="bulk">Bulk Operations</TabsTrigger>
              <TabsTrigger value="operations">Operations Log</TabsTrigger>
              <TabsTrigger value="advanced">Advanced</TabsTrigger>
            </TabsList>
            
            {/* Enhancement Sub-tab - ARCHIVED */}
            <TabsContent value="enhancement" className="mt-6 space-y-6">
              <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
                <Brain className="h-4 w-4 text-green-600" />
                <AlertTitle className="text-green-900 dark:text-green-100">
                  Enhancement System Complete
                </AlertTitle>
                <AlertDescription className="text-green-800 dark:text-green-200">
                  🎉 Successfully enhanced 596/750 modules (avg 2,684 words)! Enhancement monitoring has been archived.
                  See <code>docs/archived-systems.md</code> for details.
                </AlertDescription>
              </Alert>
              
              <MLContentQualityDashboard />
              <ContentEnhancementStats />
            </TabsContent>
            
            {/* AI Generation Sub-tab - Clean & Focused */}
            <TabsContent value="generation" className="mt-6 space-y-6">
              {!activeView && (
                <>
                  {/* Quick Access Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <Card className="border-2 border-primary/20 hover:border-primary/40 transition-colors">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="h-5 w-5" />
                          Module Generation
                        </CardTitle>
                        <CardDescription>
                          Split large content into 4-6 logical modules with quizzes
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button 
                          onClick={() => window.location.href = '/admin/module-generation'}
                          className="w-full"
                        >
                          Open Module Generator
                        </Button>
                        <p className="text-sm text-muted-foreground mt-2">
                          {moduleStats ? (
                            <>
                              <strong>{moduleStats.totalIncomplete}</strong> courses need modules 
                              ({moduleStats.withContent} have content, {moduleStats.withoutContent} need content)
                            </>
                          ) : (
                            'Loading statistics...'
                          )}
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="border-2 border-purple-500/20 hover:border-purple-500/40 transition-colors">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 justify-between">
                          <div className="flex items-center gap-2">
                            <Brain className="h-5 w-5" />
                            Audio Narration
                          </div>
                          {audioStats.missing > 0 && (
                            <Badge variant="destructive" className="animate-pulse">
                              {audioStats.missing} Missing
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription>
                          Generate professional OpenAI TTS audio for all modules
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="text-sm space-y-1">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Coverage:</span>
                              <span className="font-medium">
                                {audioStats.withAudio}/{audioStats.total} ({audioStats.total > 0 ? Math.round((audioStats.withAudio / audioStats.total) * 100) : 0}%)
                              </span>
                            </div>
                            {audioStats.missing > 0 && (
                              <>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Missing:</span>
                                  <span className="font-medium text-orange-600">{audioStats.missing} modules</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Est. Cost:</span>
                                  <span className="font-medium">$2-5</span>
                                </div>
                              </>
                            )}
                          </div>
                          
                          <Button 
                            onClick={() => window.location.href = '/admin/audio-generation'}
                            className="w-full"
                            variant={audioStats.missing > 0 ? "default" : "outline"}
                          >
                            {audioStats.missing > 0 
                              ? `Generate ${audioStats.missing} Missing Modules` 
                              : 'View Audio Dashboard'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <FixCourseAssignments />
                  
                  {/* Clean Generation Tools */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Activity className="h-5 w-5" />
                          System Health
                        </CardTitle>
                        <CardDescription>
                          Monitor quiz generation, practice labs, and content status
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button 
                          onClick={() => setSearchParams({ tab: 'configuration', subtab: 'generation', view: 'health' })}
                          className="w-full"
                        >
                          View System Health
                        </Button>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Zap className="h-5 w-5" />
                          Quiz Generation
                        </CardTitle>
                        <CardDescription>
                          Generate missing quizzes for all modules
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button 
                          onClick={() => setSearchParams({ tab: 'configuration', subtab: 'generation', view: 'quiz' })}
                          className="w-full"
                        >
                          Generate Quizzes
                        </Button>
                      </CardContent>
                    </Card>
                  </div>

                  <GenerationStatusMonitor />
                  <ContentDiagnosticsPanel />
                </>
              )}
              
              {/* System Health View */}
              {activeView === 'health' && (
                <div>
                  <Button 
                    variant="outline" 
                    className="mb-4"
                    onClick={() => setSearchParams({ tab: 'configuration', subtab: 'generation' })}
                  >
                    ← Back to Generation Tools
                  </Button>
                  <GenerationHealthDashboard />
                </div>
              )}
              
              {/* Quiz Generation View */}
              {activeView === 'quiz' && (
                <div className="space-y-6">
                  <Button 
                    variant="outline" 
                    className="mb-4"
                    onClick={() => setSearchParams({ tab: 'configuration', subtab: 'generation' })}
                  >
                    ← Back to Generation Tools
                  </Button>
                  <QuizCompletionDashboard />
                  <QuizGenerationMonitor />
                  <QuizCoverageReport />
                </div>
              )}
            </TabsContent>
            
            {/* Data Quality Sub-tab */}
            <TabsContent value="data-quality" className="mt-6 space-y-6">
              <Alert className="mb-6">
                <FileWarning className="h-4 w-4" />
                <AlertDescription>
                  Identify and fix modules with invalid titles that prevent audio generation. 
                  Invalid patterns include "Module X:", "Section X:", or other structural prefixes.
                </AlertDescription>
              </Alert>
              <ModuleDataQuality />
            </TabsContent>
            
            {/* Content Review Sub-tab */}
            <TabsContent value="review" className="mt-6 space-y-6">
              <ContentReviewPanel />
            </TabsContent>
            
            {/* Bulk Operations Sub-tab */}
            <TabsContent value="bulk" className="mt-6 space-y-6">
              <BulkOperationsPanel />
            </TabsContent>
            
            {/* Operations Log Sub-tab - ARCHIVED */}
            <TabsContent value="operations" className="mt-6">
              <Alert>
                <Brain className="h-4 w-4" />
                <AlertTitle>Operations Monitoring Archived</AlertTitle>
                <AlertDescription>
                  Enhancement and orchestration operation logs have been archived. For historical data, 
                  query the <code>content_enhancement_operations</code> and <code>*_operations</code> tables directly.
                </AlertDescription>
              </Alert>
            </TabsContent>
            
            {/* Advanced Settings Sub-tab */}
            <TabsContent value="advanced" className="mt-6 space-y-6">
              <SystemTestingPanel />
              <PerformanceOptimizationPanel />
              <ProductionReadinessPanel />
              
              <Card>
                <CardHeader>
                  <CardTitle>Advanced Configuration</CardTitle>
                  <CardDescription>
                    System-wide settings and experimental features
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Advanced system configuration settings will be available here. Future enhancements include:
                  </p>
                  <ul className="list-disc list-inside text-sm text-muted-foreground mt-4 space-y-2">
                    <li>AI model configuration and token limits</li>
                    <li>Content enhancement thresholds</li>
                    <li>Batch processing settings</li>
                    <li>System performance tuning</li>
                    <li>Feature flags and experimental options</li>
                  </ul>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Content Division Tab */}
        <TabsContent value="content-division" className="space-y-6">
          <Alert className="mb-6">
            <FileText className="h-4 w-4" />
            <AlertDescription>
              Transform large modules (2000+ words) into optimized tab-based content for better performance and user experience.
            </AlertDescription>
          </Alert>
          <ContentDivisionDashboard />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
